import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useTTS } from '../hooks/useSpeech.js';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder.js';
import FollowupEditor from '../components/FollowupEditor.jsx';
import OpportunityEditor from '../components/OpportunityEditor.jsx';
import CustomerSearchSelect from '../components/CustomerSearchSelect.jsx';
import BackButton from '../components/BackButton.jsx';

const CATEGORY_LABEL = {
  customer: '🏢 Customer', contact: '👤 Contact', product: '🚚 Product',
  logistics: '📦 Logistics', other: '💬 Other',
};

// Matches Notes.jsx's ADMIN_ROLES and the backend's sqlClauseOwned() grants —
// admins get dealership/branch-wide note access there, so the UI should offer
// the same reassign control it already does on the Notes list page.
const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];

function attachmentSrc(noteId, attachId) {
  const token = localStorage.getItem('utsc_token');
  return `/api/notes/${noteId}/attachments/${attachId}/file?token=${encodeURIComponent(token || '')}`;
}

function normalizeItem(a, fallbackOwner) {
  if (typeof a === 'string') return { text: a, owner: fallbackOwner || '', due_date: '' };
  return {
    text: a?.text || '',
    owner: (a?.owner && String(a.owner)) || fallbackOwner || '',
    due_date: a?.due_date ? String(a.due_date).slice(0, 10) : '',
  };
}

export default function NoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { speak, stop: stopSpeaking, supported: ttsSupported } = useTTS();
  const hasSpoken = useRef(false);

  // Tick every second while waiting — drives timeout display without extra state
  const [, setTick] = useState(0);
  const tickRef2 = useRef(null);

  const note = useQuery({
    queryKey: ['note', id],
    queryFn: () => api.get(`/notes/${id}`).then(r => r.data.data),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return 3000;
      if (!d.transcript || !d.ai_processed_at) return 3000;
      return false;
    },
  });

  // Drive second-level re-render while waiting so timeout thresholds update live
  useEffect(() => {
    const waiting = !note.data?.transcript || !note.data?.ai_processed_at;
    if (waiting && !tickRef2.current) {
      tickRef2.current = setInterval(() => setTick(t => t + 1), 1000);
    }
    if (!waiting && tickRef2.current) {
      clearInterval(tickRef2.current);
      tickRef2.current = null;
    }
    return () => { if (tickRef2.current) { clearInterval(tickRef2.current); tickRef2.current = null; } };
  }, [note.data?.transcript, note.data?.ai_processed_at]);

  const reprocess = useMutation({
    mutationFn: () => api.post(`/notes/${id}/reprocess`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note', id] }),
  });

  const [transcript, setTranscript] = useState('');
  useEffect(() => { if (note.data?.transcript) setTranscript(note.data.transcript); }, [note.data?.transcript]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const [noteDate, setNoteDate] = useState('');
  const [datePendingConfirm, setDatePendingConfirm] = useState(false);
  useEffect(() => { if (note.data?.note_date) setNoteDate(String(note.data.note_date).slice(0, 10)); }, [note.data?.note_date]);

  function handleNoteDateChange(val) {
    setNoteDate(val);
    setDatePendingConfirm(true);
  }
  function saveNoteDate() {
    save.mutate({ note_date: noteDate }, { onSuccess: () => setDatePendingConfirm(false) });
  }

  // No auto-speak on review: opening a note must be silent. Playback of the
  // summary is opt-in via the 🔊 button next to it. (Auto-speaking when the
  // note loads was jarring — it "started talking" as soon as you opened it.)

  const [followupSeed, setFollowupSeed] = useState(null);
  const [oppSeed, setOppSeed] = useState(null);

  const save = useMutation({
    mutationFn: (data) => api.put(`/notes/${id}`, data).then(r => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note', id] }),
  });

  // Always-available customer reassignment (not just when unassigned) — see
  // POST /notes/:id/link-customer, which now cascades to follow_ups/
  // opportunities/agent_conversations still pointing at the old customer.
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [localCustomer, setLocalCustomer] = useState(null); // { id, name, hint } while the picker is open
  const linkCustomer = useMutation({
    mutationFn: ({ customerId, nameHint } = {}) => api.post(`/notes/${id}/link-customer`,
      nameHint ? { customer_name_hint: nameHint } : { customer_id: customerId }
    ).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['note', id] });
      qc.invalidateQueries({ queryKey: ['notes'] });
      setEditingCustomer(false);
      setLocalCustomer(null);
    },
  });

  const dismissCommitments = useMutation({
    mutationFn: () => api.post(`/notes/${id}/dismiss-commitments`).then(r => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note', id] }),
  });

  const deleteNote = useMutation({
    mutationFn: () => api.delete(`/notes/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notes'] });
      navigate('/notes');
    },
  });

  const [actionItems, setActionItems] = useState([]);
  const [commitErr, setCommitErr] = useState(null);
  useEffect(() => {
    const raw = note.data?.structured?.action_items;
    setActionItems(Array.isArray(raw) ? raw.map(a => normalizeItem(a, note.data?.user_name)) : []);
  }, [note.data?.ai_processed_at, note.data?.user_name, id]);

  function updateItem(i, patch) {
    setActionItems(prev => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    setCommitErr(null);
  }

  if (note.isLoading) return <div className="text-muted">Loading…</div>;
  if (!note.data) return <div className="text-crimson">Not found.</div>;

  const n = note.data;
  const s = n.structured || {};
  const isAuthor = user?.id === n.user_id;
  const canReassignCustomer = isAuthor || ADMIN_ROLES.includes(user?.role);
  const allCommitmentsComplete = actionItems.length > 0 && actionItems.every(a => a.owner.trim() && a.due_date);

  function saveCommitments() {
    setCommitErr(null);
    save.mutate(
      { structured: { ...s, action_items: actionItems.map(a => ({ text: a.text, owner: a.owner.trim(), due_date: a.due_date })) } },
      { onError: (err) => setCommitErr(err?.response?.data?.error || 'Could not save action items.') }
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <BackButton fallback="/notes" />
      <header>
        <div className="section-eyebrow">Note</div>
        {editingCustomer ? (
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <CustomerSearchSelect
                value={localCustomer?.id}
                valueLabel={localCustomer?.name}
                hintValue={localCustomer?.hint}
                suggestRecent
                onChange={(cid, name, isHint) => {
                  if (isHint) {
                    linkCustomer.mutate({ nameHint: name });
                  } else if (cid) {
                    linkCustomer.mutate({ customerId: cid });
                  } else {
                    setLocalCustomer({ id: null, name: '', hint: '' });
                  }
                }}
              />
              {linkCustomer.isError && (
                <div className="text-crimson text-xs mt-1">Failed to save — try again.</div>
              )}
            </div>
            <button
              type="button"
              className="text-xs text-muted hover:underline mt-2"
              onClick={() => { setEditingCustomer(false); setLocalCustomer(null); }}
              disabled={linkCustomer.isPending}
            >
              Cancel
            </button>
          </div>
        ) : (
          <h1 className="text-2xl flex items-center gap-2">
            <span>{n.customer_name || n.customer_name_hint || s.customer_name || s.customer_name_guess || '(unassigned)'}</span>
            {n.customer_name && (
              <span className="inline-flex items-center rounded px-1 py-0.5 bg-emerald-100 align-middle" title="Linked">🔗</span>
            )}
            {!n.customer_name && (n.customer_name_hint || s.customer_name || s.customer_name_guess) && (
              <span className="inline-flex items-center rounded px-1 py-0.5 bg-amber-100 align-middle" title="Unlinked">🔗</span>
            )}
            {canReassignCustomer && (
              <button
                type="button"
                className="text-navy/50 hover:text-navy text-sm"
                title="Change customer"
                onClick={() => { setLocalCustomer({ id: n.customer_id || null, name: n.customer_name || '', hint: n.customer_id ? '' : (n.customer_name_hint || '') }); setEditingCustomer(true); }}
              >
                ✏️
              </button>
            )}
          </h1>
        )}
        <div className="rule-crimson" />
        <div className="text-xs text-muted flex items-center flex-wrap gap-2">
          <span>
            {isAuthor ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-navy/60">Activity:</span>
                <input
                  type="date"
                  value={noteDate}
                  max={todayISO}
                  onChange={e => handleNoteDateChange(e.target.value)}
                  className="bg-transparent text-xs underline cursor-pointer border-none outline-none text-muted"
                  disabled={save.isPending}
                />
              </span>
            ) : (
              <span>Activity: {noteDate ? new Date(noteDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
            )}
            {' '}· recorded {new Date(n.created_at).toLocaleDateString()}
            {' '}·{' '}
            {user?.id === n.user_id ? (
              <select
                className="bg-transparent text-xs underline cursor-pointer border-none outline-none text-muted"
                value={n.touch_type || 'note'}
                onChange={e => save.mutate({ touch_type: e.target.value })}
                disabled={save.isPending}
              >
                {['visit','call','email','quote','note','photo'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            ) : (
              n.touch_type || n.source
            )}
          </span>
          {n.source === 'email' ? (
            <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-900 text-[10px] uppercase tracking-wide">✉️ From email</span>
          ) : (
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wide">
              {n.source === 'voice' ? '🎙️ Voice (app)' : '⌨️ Typed (app)'}
            </span>
          )}
          {n.audio_duration_sec ? <span>· {n.audio_duration_sec}s</span> : null}
          {n.user_name && <span>· by <span className="text-navy">{n.user_name}</span></span>}
          {n.follow_up_id && (
            <Link to={`/followups/${n.follow_up_id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-900 text-[10px] uppercase tracking-wide hover:bg-sky-200">
              📋 {n.reply_to_title ? `For follow-up: ${n.reply_to_title}` : 'For follow-up'}
            </Link>
          )}
          {n.visibility === 'private' ? (
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] uppercase tracking-wide">🔒 Private</span>
          ) : (
            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 text-[10px] uppercase tracking-wide">👥 Team</span>
          )}
          {user?.id === n.user_id && (
            <button className="text-[11px] underline text-navy/70 hover:text-crimson"
                    onClick={() => save.mutate({ visibility: n.visibility === 'team' ? 'private' : 'team' })}
                    disabled={save.isPending}>
              {n.visibility === 'team' ? 'Make private' : 'Share with team'}
            </button>
          )}
          {user?.id === n.user_id && (
            <button
              className="text-[11px] underline text-crimson/60 hover:text-crimson"
              disabled={deleteNote.isPending}
              onClick={() => {
                if (window.confirm('Delete this note? This cannot be undone.')) {
                  deleteNote.mutate();
                }
              }}
            >
              {deleteNote.isPending ? 'Deleting…' : 'Delete note'}
            </button>
          )}
        </div>
      </header>

      {datePendingConfirm && isAuthor && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <div className="font-medium mb-2">
            Change activity date to {noteDate ? new Date(noteDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : noteDate}?
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-1 rounded bg-navy text-white text-xs font-medium hover:bg-navy/80 disabled:opacity-50"
                    disabled={save.isPending} onClick={saveNoteDate}>
              {save.isPending ? 'Saving…' : 'Yes, save date'}
            </button>
            <button className="px-3 py-1 rounded border border-amber-300 text-amber-800 text-xs hover:bg-amber-100"
                    onClick={() => { setNoteDate(String(n.note_date).slice(0, 10)); setDatePendingConfirm(false); }}>
              Cancel
            </button>
          </div>
          {save.isError && <div className="text-crimson text-xs mt-1">{save.error?.response?.data?.error || 'Save failed'}</div>}
        </div>
      )}

      <CustomerLinkPrompt note={n} onSettled={() => qc.invalidateQueries({ queryKey: ['note', id] })} />

      {n.source === 'email' && (
        <section className="card p-4">
          <div className="section-eyebrow">Original email</div>
          <div className="rule-crimson" />
          {s.source_subject && <div className="text-sm font-medium text-navy mt-2">Subject: {s.source_subject}</div>}
          {s.email_body ? (
            <details className="mt-2">
              <summary className="text-sm text-crimson cursor-pointer hover:underline">View original email</summary>
              <pre className="mt-2 text-xs whitespace-pre-wrap font-sans text-ink bg-panel rounded p-3 max-h-96 overflow-y-auto">{s.email_body}</pre>
            </details>
          ) : (
            <div className="text-xs text-muted mt-2">
              The original email text wasn't stored for this note — it was ingested before email-body capture was added (2026-06-26). New email notes from now on will include it.
            </div>
          )}
        </section>
      )}

      <section className="card p-4">
        <div className="section-eyebrow">Transcript</div>
        <div className="rule-crimson" />
        {!n.transcript && (() => {
          const ageMs = Date.now() - new Date(n.created_at).getTime();
          const timedOut = ageMs > 120_000; // 2 min
          return timedOut ? (
            <div className="mt-2 space-y-1">
              <div className="text-crimson text-sm font-medium">Transcription failed — tap to retry</div>
              <button className="btn-ghost text-sm" disabled={reprocess.isPending}
                      onClick={() => reprocess.mutate()}>
                {reprocess.isPending ? 'Retrying…' : 'Retry transcription'}
              </button>
              {reprocess.isError && <div className="text-crimson text-xs">Retry failed. Try again.</div>}
            </div>
          ) : (
            <div className="text-muted text-sm mt-2 flex items-center gap-2">
              <span className="animate-spin text-base">⟳</span> Transcribing…
            </div>
          );
        })()}
        {n.transcript && !n.ai_processed_at && (() => {
          const ageMs = Date.now() - new Date(n.created_at).getTime();
          const timedOut = ageMs > 180_000; // 3 min — AI should be done by now
          if (!timedOut) return null;
          return (
            <div className="mt-2 bg-amber-50 border border-amber-300 rounded p-2 flex items-center justify-between gap-2">
              <span className="text-amber-800 text-sm">AI processing taking longer than expected</span>
              <button className="btn-ghost text-xs px-2 py-1" disabled={reprocess.isPending}
                      onClick={() => reprocess.mutate()}>
                {reprocess.isPending ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          );
        })()}
        {n.transcript && user?.id === n.user_id && (
          <>
            <textarea className="input min-h-[120px] mt-2" value={transcript}
                      onChange={e => setTranscript(e.target.value)}
                      spellCheck autoCorrect="on" autoCapitalize="sentences" />
            <div className="flex justify-end mt-2">
              <button className="btn-ghost" disabled={transcript === n.transcript || save.isPending}
                      onClick={() => save.mutate({ transcript })}>Save edits</button>
            </div>
          </>
        )}
        {n.transcript && user?.id !== n.user_id && (
          <div className="mt-2 text-sm whitespace-pre-wrap">{n.transcript}</div>
        )}
      </section>

      <Attachments noteId={n.id} />

      {n.ai_processed_at && user?.id === n.user_id && !n.commitments_resolved_at
       && (s.action_items?.length > 0 || s.suggested_followup?.title)
       && n.followup_count === 0 && (
        <div className="rounded border-l-4 border-crimson bg-crimson/5 p-3 text-sm flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-crimson">Commit before closing</div>
            <div className="text-navy mt-1">
              {s.action_items?.length || 0} action item{s.action_items?.length === 1 ? '' : 's'} found
              {s.suggested_followup?.title ? ' and a suggested follow-up' : ''}. Promote to a follow-up below or dismiss.
            </div>
          </div>
          <button className="btn-ghost text-xs px-2 py-1 whitespace-nowrap"
                  disabled={dismissCommitments.isPending} onClick={() => dismissCommitments.mutate()}>
            Dismiss
          </button>
        </div>
      )}

      {/* Clarifying questions — shown BEFORE AI structure so they're seen first */}
      <AiClarifications noteId={n.id} speak={speak} ttsSupported={ttsSupported} />

      {n.ai_processed_at && (
        <section className="card p-4">
          <div className="section-eyebrow">AI Structure</div>
          <div className="rule-crimson" />
          <div className="text-sm space-y-2 mt-2">
            <div className="flex items-start gap-2">
              <div className="flex-1"><b>Summary:</b> {s.summary || '—'}</div>
              {ttsSupported && s.summary && (
                <div className="flex gap-1 shrink-0">
                  <button title="Read aloud" className="text-navy/50 hover:text-navy text-base leading-none"
                          onClick={() => speak(s.summary)}>🔊</button>
                  <button title="Stop" className="text-navy/40 hover:text-crimson text-xs leading-none"
                          onClick={stopSpeaking}>■</button>
                </div>
              )}
            </div>
            <div><b>Sentiment:</b> {s.sentiment || '—'} · <b>Confidence:</b> {s.confidence || '—'}</div>
            {s.high_alert && <div className="text-crimson font-semibold">⚠ High alert</div>}
            {s.topics?.length > 0 && <div><b>Topics:</b> {s.topics.join(', ')}</div>}
            {actionItems.length > 0 && (
              <div>
                <b>Action items:</b>
                <span className="text-xs text-muted"> — each needs an owner + due date before saving</span>
                <ul className="list-none pl-0 mt-1 space-y-2">
                  {actionItems.map((a, i) => (
                    <li key={i} className="bg-panel/60 rounded px-2 py-2 space-y-1.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="flex-1">{a.text}</span>
                        <button className="text-xs text-crimson hover:underline whitespace-nowrap"
                                onClick={() => setFollowupSeed({ title: a.text, due_date: a.due_date || undefined, customer_id: n.customer_id || '', customer_name_hint: n.customer_id ? '' : (n.customer_name_hint || ''), note_id: n.id, touch_type: n.touch_type || 'note' })}>
                          + Follow-up
                        </button>
                      </div>
                      {isAuthor ? (
                        <div className="grid grid-cols-2 gap-2">
                          <input className="input text-sm" placeholder="Owner *" value={a.owner}
                                 onChange={e => updateItem(i, { owner: e.target.value })} />
                          <input className="input text-sm" type="date" value={a.due_date}
                                 onChange={e => updateItem(i, { due_date: e.target.value })} />
                        </div>
                      ) : (
                        <div className="text-xs text-muted">
                          {a.owner ? `Owner: ${a.owner}` : 'No owner'} · {a.due_date ? `Due ${a.due_date}` : 'No due date'}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {isAuthor && (
                  <div className="flex items-center justify-end gap-2 mt-2">
                    {commitErr && <span className="text-crimson text-xs flex-1">{commitErr}</span>}
                    {!allCommitmentsComplete && !commitErr && (
                      <span className="text-muted text-xs flex-1">Add an owner and due date to every action item to save.</span>
                    )}
                    <button className="btn-ghost text-sm whitespace-nowrap"
                            disabled={!allCommitmentsComplete || save.isPending} onClick={saveCommitments}>
                      {save.isPending ? 'Saving…' : 'Save action items'}
                    </button>
                  </div>
                )}
              </div>
            )}
            {s.suggested_followup?.title && n.followup_count === 0 && (
              <div className="bg-panel p-2 rounded">
                <b>Suggested follow-up:</b> {s.suggested_followup.title}
                {s.suggested_followup.date_iso && <> on <i>{s.suggested_followup.date_iso}</i></>}
                {' '}({s.suggested_followup.urgency})
              </div>
            )}
            {n.pipeline_suggestion && (
              <div className="bg-amber-50 border border-amber-200 p-2 rounded text-amber-900">
                <b>This looks like{n.pipeline_suggestion.domain && n.pipeline_suggestion.domain !== 'other' ? ` a ${n.pipeline_suggestion.domain.replace('_', ' ')}` : ' an'} opportunity</b> — add it to the pipeline?
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-panelDark">
            {s.suggested_followup?.title && n.followup_count === 0 && (
              <button className="btn-primary text-sm"
                      onClick={() => setFollowupSeed({ title: s.suggested_followup.title, due_date: s.suggested_followup.date_iso, urgency: s.suggested_followup.urgency, customer_id: n.customer_id || '', customer_name_hint: n.customer_id ? '' : (n.customer_name_hint || ''), note_id: n.id, touch_type: n.touch_type || 'note' })}>
                + Promote suggestion
              </button>
            )}
            <button className="btn-ghost text-sm"
                    onClick={() => setFollowupSeed({ title: s.summary ? `Follow up: ${s.summary.slice(0, 80)}` : '', customer_id: n.customer_id || '', customer_name_hint: n.customer_id ? '' : (n.customer_name_hint || ''), note_id: n.id, touch_type: n.touch_type || 'note' })}>
              + Add follow-up
            </button>
            <button className={n.pipeline_suggestion ? 'btn-primary text-sm' : 'btn-ghost text-sm'}
                    onClick={() => setOppSeed({ name: (n.customer_name || n.customer_name_hint) ? `${n.customer_name || n.customer_name_hint} — ${(s.topics?.[0] || 'opportunity')}` : (s.summary?.slice(0, 80) || 'New opportunity'), description: s.summary || '', customer_id: n.customer_id || '', customer_name_hint: n.customer_id ? '' : (n.customer_name_hint || ''), source_note_id: n.id, domain: n.pipeline_suggestion?.domain || undefined })}>
              + Add to pipeline
            </button>
          </div>
        </section>
      )}

      <NoteFollowups noteId={n.id} noteUserId={n.user_id} />

      {followupSeed && <FollowupEditor initial={followupSeed} onClose={() => setFollowupSeed(null)} onSaved={() => setFollowupSeed(null)} />}
      {oppSeed && <OpportunityEditor initial={oppSeed} onClose={() => setOppSeed(null)} onSaved={() => setOppSeed(null)} />}
    </div>
  );
}

// ---- Clarifying questions panel ----
// Shows questions one at a time in a conversational flow.
// Speaks each question aloud when it becomes active.
// Voice answers use useVoiceRecorder + /api/notes/transcribe (works on iOS).
// TTS on iOS requires a user gesture — if auto-speak is blocked, the "Tap to hear" button serves as the unlock.

function AiClarifications({ noteId, speak, ttsSupported }) {
  const qc = useQueryClient();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [ttsUnlocked, setTtsUnlocked] = useState(false);
  const spokenIds = useRef(new Set());

  const questions = useQuery({
    queryKey: ['ai-questions', 'pending', noteId],
    queryFn: () => api.get(`/ai-questions?status=pending&note_id=${noteId}`).then(r => r.data.data),
    refetchInterval: 8000,
  });

  const current = questions.data?.[currentIdx] ?? null;

  // No auto-speak of clarifying questions either — playback is opt-in via the
  // 🔊 button. (Previously these spoke automatically once TTS was unlocked,
  // which made the note "start talking" on open.)

  function handleAnswered() {
    qc.invalidateQueries({ queryKey: ['ai-questions', 'pending', noteId] });
    // Answering may have linked a customer to the note — refresh the note view.
    qc.invalidateQueries({ queryKey: ['note'] });
    // Advance to next question; the useEffect above will speak it
    setCurrentIdx(i => i + 1);
  }

  function handleDismissed() {
    qc.invalidateQueries({ queryKey: ['ai-questions', 'pending', noteId] });
    setCurrentIdx(i => i + 1);
  }

  function unlockAndSpeak() {
    setTtsUnlocked(true);
    if (current) {
      spokenIds.current.add(current.id);
      speak(current.question_text);
    }
  }

  if (!questions.data?.length) return null;
  if (!current) return null; // all answered/dismissed

  const remaining = questions.data.length - currentIdx;

  return (
    <section className="card p-4 border-l-4 border-amber-400">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="section-eyebrow">Follow-up question</div>
          {current.category && (
            <span className="text-[10px] uppercase tracking-wider text-amber-900 bg-amber-100 rounded px-1.5 py-0.5">
              {CATEGORY_LABEL[current.category] || current.category}
            </span>
          )}
          {remaining > 1 && (
            <span className="text-xs bg-amber-100 text-amber-900 rounded px-1.5 py-0.5">{remaining} remaining</span>
          )}
        </div>
        {ttsSupported && ttsUnlocked && (
          <button title="Repeat question" className="text-amber-600 hover:text-amber-900 text-lg"
                  onClick={() => speak(current.question_text)}>🔊</button>
        )}
      </div>
      <p className="text-xs text-muted mb-3">Your answer helps refine the note's action items.</p>

      {ttsSupported && !ttsUnlocked && (
        <button
          className="w-full mb-3 rounded-lg bg-amber-100 border border-amber-300 text-amber-900 py-3 text-sm font-medium flex items-center justify-center gap-2 active:bg-amber-200"
          onClick={unlockAndSpeak}
        >
          🔊 Tap to hear question
        </button>
      )}

      <QuestionCard
        key={current.id}
        question={current}
        onAnswered={handleAnswered}
        onDismissed={handleDismissed}
      />
    </section>
  );
}

function QuestionCard({ question, onAnswered, onDismissed }) {
  const rec = useVoiceRecorder();
  const [answer, setAnswer] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeErr, setTranscribeErr] = useState(null);
  const [voiceDraft, setVoiceDraft] = useState(null); // text from voice, shown for confirmation
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerNameHint, setCustomerNameHint] = useState('');
  const [domain, setDomain] = useState('');
  // Offer the customer lookup when this note isn't linked to a customer yet.
  const canLinkCustomer = !question.customer_id;
  // Domain picker only for domain-category questions — unlike the customer
  // lookup above, showing this on every question would be noise (a note's
  // department being unset is normal and usually unrelated to the question).
  const canSetDomain = question.category === 'domain' && !question.domain;

  // When recording stops, transcribe it
  useEffect(() => {
    if (!rec.blob) return;
    (async () => {
      setTranscribing(true);
      setTranscribeErr(null);
      try {
        const fd = new FormData();
        fd.append('audio', rec.blob, `answer-${Date.now()}.webm`);
        const r = await api.post('/notes/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const text = r.data.data?.text || '';
        setVoiceDraft(text);
        setAnswer(text);
      } catch {
        setTranscribeErr('Could not transcribe. Edit below or re-record.');
      } finally {
        setTranscribing(false);
      }
    })();
  }, [rec.blob]);

  const submit = useMutation({
    mutationFn: () => api.post(`/ai-questions/${question.id}/answer`, {
      answer,
      ...(customerId ? { customer_id: customerId } : {}),
      ...(customerNameHint ? { customer_name_hint: customerNameHint } : {}),
      ...(domain ? { domain } : {}),
    }).then(r => r.data),
    onSuccess: onAnswered,
  });

  const dismiss = useMutation({
    mutationFn: () => api.post(`/ai-questions/${question.id}/dismiss`).then(r => r.data),
    onSuccess: onDismissed,
  });

  const busy = submit.isPending || dismiss.isPending || transcribing;
  const canSubmit = (answer.trim() || customerId || customerNameHint || domain) && !busy;

  function reRecord() {
    rec.reset();
    setVoiceDraft(null);
    setAnswer('');
    setTranscribeErr(null);
  }

  return (
    <div className="space-y-3">
      {/* Question text */}
      <p className="text-base font-medium text-navy leading-snug">{question.question_text}</p>

      {/* Voice recording section */}
      <div className="bg-amber-50 rounded-lg p-3">
        {!rec.blob && !transcribing && (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={rec.recording ? rec.stop : rec.start}
              disabled={busy}
              className={`rounded-full w-16 h-16 text-3xl shadow transition ${rec.recording ? 'bg-crimson text-white animate-pulse' : 'bg-navy text-white'}`}
            >
              {rec.recording ? '■' : '🎙'}
            </button>
            <div className="text-xs text-muted">
              {rec.recording ? `Recording… ${formatDuration(rec.durationSec)}` : 'Tap to answer by voice'}
            </div>
          </div>
        )}

        {transcribing && (
          <div className="text-center text-sm text-amber-700 py-2">Transcribing…</div>
        )}

        {rec.blob && !transcribing && (
          <div className="space-y-2">
            {voiceDraft && (
              <div className="text-xs text-muted">Heard: "{voiceDraft}" — edit below if needed</div>
            )}
            <button className="text-xs text-navy underline" onClick={reRecord}>Re-record</button>
          </div>
        )}

        {transcribeErr && (
          <div className="text-crimson text-xs mt-1">{transcribeErr}</div>
        )}
        {rec.error && (
          <div className="text-crimson text-xs mt-1">{rec.error}</div>
        )}
      </div>

      {/* Customer lookup — when the note isn't linked to a customer yet */}
      {canLinkCustomer && (
        <div>
          <label className="label text-xs">Link the customer this note is about</label>
          <CustomerSearchSelect
            value={customerId} valueLabel={customerName} hintValue={customerNameHint}
            onChange={(id, name, isHint) => {
              if (isHint) { setCustomerId(null); setCustomerName(''); setCustomerNameHint(name); }
              else { setCustomerId(id); setCustomerName(name || ''); setCustomerNameHint(''); }
            }}
          />
        </div>
      )}

      {/* Department picker — domain-category questions only */}
      {canSetDomain && (
        <div>
          <label className="label text-xs">Department</label>
          <select className="input" value={domain} onChange={e => setDomain(e.target.value)}>
            <option value="">— Select —</option>
            <option value="parts">Parts</option>
            <option value="service">Service</option>
            <option value="trailer_sales">Trailer sales</option>
            <option value="other">Other</option>
          </select>
        </div>
      )}

      {/* Text answer — always editable; pre-filled after voice transcription */}
      <div>
        <label className="label text-xs">Your answer</label>
        <textarea
          className="input text-sm min-h-[72px]"
          placeholder={canLinkCustomer ? 'Type an answer, use the mic, or just link the customer above…' : 'Type your answer, or use the mic above…'}
          value={answer}
          onChange={e => { setAnswer(e.target.value); setVoiceDraft(null); }}
          disabled={busy}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <button className="btn-ghost text-sm px-3 py-1.5" disabled={busy}
                onClick={() => dismiss.mutate()}>
          Skip
        </button>
        <button className="btn-primary text-sm px-4 py-1.5" disabled={!canSubmit}
                onClick={() => submit.mutate()}>
          {submit.isPending ? 'Sending…'
            : (customerId || customerNameHint) && !answer.trim() ? 'Link customer'
            : domain && !answer.trim() ? 'Set department'
            : 'Submit'}
        </button>
      </div>
      {submit.isError && (
        <div className="text-crimson text-xs">{submit.error?.response?.data?.error || 'Failed to submit'}</div>
      )}
    </div>
  );
}

function formatDuration(s) {
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// ---- Follow-ups linked to this note ----
const URGENCY_COLOR = { high: 'text-crimson', medium: 'text-amber-700', low: 'text-muted' };
const STATUS_LABEL = { pending: 'Pending', done: 'Done', snoozed: 'Snoozed', missed: 'Overdue' };

function NoteFollowups({ noteId, noteUserId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editTarget, setEditTarget] = useState(null);

  const followups = useQuery({
    queryKey: ['followups', 'note', noteId],
    queryFn: () => api.get(`/followups?note_id=${noteId}&status=all`).then(r => r.data.data),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/followups/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followups', 'note', noteId] });
      qc.invalidateQueries({ queryKey: ['note', String(noteId)] });
    },
  });

  const items = followups.data || [];
  if (!followups.isSuccess || items.length === 0) return null;

  const canEdit = (fu) => user?.id === fu.user_id || ['superadmin', 'dealer_admin', 'branch_admin'].includes(user?.role);

  return (
    <section className="card p-4">
      <div className="section-eyebrow">Follow-ups</div>
      <div className="rule-crimson" />
      <ul className="space-y-2 mt-2">
        {items.map(fu => (
          <li key={fu.id} className="flex items-start justify-between gap-3 bg-panel/60 rounded px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-navy truncate">{fu.title}</span>
                {fu.source === 'ai' && (
                  <span className="text-[10px] uppercase tracking-wide bg-sky-100 text-sky-800 rounded px-1.5 py-0.5">AI</span>
                )}
                <span className={`text-xs ${URGENCY_COLOR[fu.urgency] || 'text-muted'}`}>{fu.urgency}</span>
              </div>
              <div className="text-xs text-muted mt-0.5 flex items-center gap-2">
                <span>{STATUS_LABEL[fu.status] || fu.status}</span>
                <span>·</span>
                <span>Due {new Date(fu.due_date).toLocaleDateString()}</span>
                {fu.user_name && user?.id !== fu.user_id && <><span>·</span><span>{fu.user_name}</span></>}
              </div>
              {fu.description && <div className="text-xs text-muted mt-1 line-clamp-1">{fu.description}</div>}
            </div>
            {canEdit(fu) && (
              <div className="flex gap-1 shrink-0">
                <button
                  className="text-navy/50 hover:text-navy text-xs px-2 py-1 rounded hover:bg-panel"
                  title="Edit"
                  onClick={() => setEditTarget(fu)}
                >
                  ✏️
                </button>
                <button
                  className="text-crimson/50 hover:text-crimson text-xs px-2 py-1 rounded hover:bg-panel"
                  title="Delete"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm('Delete this follow-up? This cannot be undone.')) {
                      remove.mutate(fu.id);
                    }
                  }}
                >
                  🗑
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {editTarget && (
        <FollowupEditor
          initial={{
            id: editTarget.id,
            title: editTarget.title,
            description: editTarget.description || '',
            due_date: editTarget.due_date,
            urgency: editTarget.urgency,
            customer_id: editTarget.customer_id || '',
            note_id: editTarget.note_id,
            touch_type: editTarget.touch_type || 'note',
          }}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            qc.invalidateQueries({ queryKey: ['followups', 'note', noteId] });
          }}
        />
      )}
    </section>
  );
}

// ---- Attachments ----
function Attachments({ noteId }) {
  const qc = useQueryClient();
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const list = useQuery({
    queryKey: ['note', String(noteId), 'attachments'],
    queryFn: () => api.get(`/notes/${noteId}/attachments`).then(r => r.data.data),
  });
  const upload = useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      return api.post(`/notes/${noteId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note', String(noteId), 'attachments'] }),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/notes/${noteId}/attachments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note', String(noteId), 'attachments'] }),
  });

  function pickFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length) upload.mutate(files);
    e.target.value = '';
  }

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="section-eyebrow">Photos & files</div>
          <div className="rule-crimson" />
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost text-sm" onClick={() => cameraRef.current?.click()} disabled={upload.isPending}>📷 Camera</button>
          <button className="btn-ghost text-sm" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>📎 Upload</button>
        </div>
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFiles} />
      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pickFiles} />

      {upload.isPending && <div className="text-muted text-sm mt-2">Uploading…</div>}
      {upload.isError && <div className="text-crimson text-sm mt-2">Upload failed.</div>}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
        {list.data?.map(a => (
          <div key={a.id} className="relative group">
            {a.kind === 'image' ? (
              <a href={attachmentSrc(noteId, a.id)} target="_blank" rel="noreferrer">
                <img src={attachmentSrc(noteId, a.id)} alt={a.original_name || ''}
                     className="w-full h-24 object-cover rounded border border-panelDark" />
              </a>
            ) : (
              <a href={attachmentSrc(noteId, a.id)} target="_blank" rel="noreferrer"
                 className="w-full h-24 bg-panel rounded border border-panelDark flex items-center justify-center text-xs text-navy text-center px-2">
                📄 {a.original_name || 'file'}
              </a>
            )}
            <button onClick={() => { if (window.confirm('Delete this attachment?')) remove.mutate(a.id); }}
                    className="absolute top-1 right-1 bg-white/80 text-crimson text-xs rounded px-1 opacity-0 group-hover:opacity-100">
              ✕
            </button>
          </div>
        ))}
        {list.data?.length === 0 && (
          <div className="col-span-full text-muted text-sm">No photos or files yet.</div>
        )}
      </div>
    </section>
  );
}

// Shown on a note that has no customer_id but has AI-found candidates.
// Single match: "Is this note about X in City? Yes / Not this customer"
// Multiple:     list of buttons + "None of these"
function CustomerLinkPrompt({ note, onSettled }) {
  const candidates = note?.structured?.customer_candidates;
  if (note?.customer_id || !Array.isArray(candidates) || candidates.length === 0) return null;

  const link = useMutation({
    mutationFn: (customerId) =>
      api.post(`/notes/${note.id}/link-customer`, { customer_id: customerId }).then(r => r.data),
    onSuccess: onSettled,
  });

  const isSingle = candidates.length === 1;
  const c = candidates[0];

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
      <div className="font-medium text-amber-900 mb-2">
        {isSingle
          ? <>Is this note about <span className="font-semibold">{c.name}</span>{c.city ? <> in {c.city}</> : null}?</>
          : 'Which customer is this note about?'}
      </div>

      {isSingle ? (
        <div className="flex gap-2">
          <button
            className="px-3 py-1 rounded bg-navy text-white text-xs font-medium hover:bg-navy/80 disabled:opacity-50"
            disabled={link.isPending}
            onClick={() => link.mutate(c.id)}
          >
            {link.isPending ? 'Linking…' : `Yes, ${c.name}`}
          </button>
          <button
            className="px-3 py-1 rounded border border-amber-300 text-amber-800 text-xs hover:bg-amber-100 disabled:opacity-50"
            disabled={link.isPending}
            onClick={() => link.mutate(null)}
          >
            Not this customer
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {candidates.map(candidate => (
            <button
              key={candidate.id}
              className="text-left px-3 py-1.5 rounded border border-amber-200 bg-white text-amber-900 text-xs hover:bg-amber-100 disabled:opacity-50"
              disabled={link.isPending}
              onClick={() => link.mutate(candidate.id)}
            >
              <span className="font-medium">{candidate.name}</span>
              {candidate.city && <span className="text-amber-600"> · {candidate.city}</span>}
              {candidate.account_number && <span className="text-amber-500"> · #{candidate.account_number}</span>}
            </button>
          ))}
          <button
            className="text-xs text-amber-600 hover:underline mt-0.5 text-left disabled:opacity-50"
            disabled={link.isPending}
            onClick={() => link.mutate(null)}
          >
            None of these
          </button>
        </div>
      )}

      {link.isError && (
        <div className="text-crimson text-xs mt-2">Failed to save — try again.</div>
      )}
    </div>
  );
}
